import React, { useMemo } from 'react';
import {
  AutomationsFieldDescription,
  AutomationsFieldLabel,
  AutomationsFieldContainer,
} from 'pages/Automations/components/Layout';
import { Box, Text, TextInput, Select, TimePicker, SelectOptionProps } from '@televet/kibble-ui';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { parseISO, set } from 'date-fns';
import { updateAutomationSendTiming } from 'pages/Automations/state/automationsSlice';
import { TimeIncrement } from 'pages/Automations/types/AutomationDraft';
import { formatPluralization } from 'shared/utils';

export const LapsedConditions = (): JSX.Element => {
  const sendTiming = useAppSelector((state) => state.automations.automationDraft.sendTiming);
  const dispatch = useAppDispatch();

  const handleTimeOfDayChange = (time: Date): void => {
    dispatch(updateAutomationSendTiming({ timeOfDay: time.toISOString() }));
  };

  const handleTimeIncrementChange = (option: SelectOptionProps): void => {
    dispatch(updateAutomationSendTiming({ timeIncrement: option.value as TimeIncrement }));
  };

  const handleTimeValueChange: React.ChangeEventHandler<HTMLInputElement> = (event): void => {
    const parsed = parseInt(event.target.value);
    const timeValue = isNaN(parsed) ? null : parsed;
    dispatch(updateAutomationSendTiming({ timeValue: timeValue }));
  };

  const pluralizedTimeIncrements = useMemo(() => {
    const defaultTimeIncrements = {
      days: 'Days',
      weeks: 'Weeks',
      months: 'Months',
    };

    if (!sendTiming.timeValue || sendTiming.timeValue === 1) return defaultTimeIncrements;

    return {
      days: formatPluralization({ word: 'Day', singularCriteria: sendTiming.timeValue }),
      weeks: formatPluralization({ word: 'Week', singularCriteria: sendTiming.timeValue }),
      months: formatPluralization({ word: 'Month', singularCriteria: sendTiming.timeValue }),
    };
  }, [sendTiming]);

  return (
    <>
      <AutomationsFieldContainer>
        <AutomationsFieldLabel>Lapsed Client Conditions</AutomationsFieldLabel>
        <AutomationsFieldDescription>
          This automation will trigger for clients who meet the following criteria:
        </AutomationsFieldDescription>
        <Box border="1px" borderColor="border.default" py={2} px={4} bg="background.subtle" borderRadius="md">
          <Text mr={2}>Has not had an appointment in</Text>
          <TextInput
            type="number"
            value={sendTiming.timeValue || undefined}
            onChange={handleTimeValueChange}
            formControlStyle={{ width: 'max-content', display: 'inline-block', mr: 2 }}
            size="sm"
            maxW="52px"
          />
          <Select
            formControlStyle={{ width: 'max-content', display: 'inline-block', mr: 2 }}
            size="sm"
            value={sendTiming.timeIncrement || TimeIncrement.Months}
            options={[
              { label: pluralizedTimeIncrements?.days, value: TimeIncrement.Days },
              { label: pluralizedTimeIncrements?.weeks, value: TimeIncrement.Weeks },
              { label: pluralizedTimeIncrements?.months, value: TimeIncrement.Months },
            ]}
            listProps={{ onSelect: handleTimeIncrementChange }}
            maxW="112px"
          />
          <Text>and has no active appointments scheduled in the future.</Text>
        </Box>
      </AutomationsFieldContainer>
      <AutomationsFieldContainer>
        <AutomationsFieldLabel>Send Timing</AutomationsFieldLabel>
        <AutomationsFieldDescription>
          When triggered, this automation will send at the following time:
        </AutomationsFieldDescription>
        <Box maxW="140px" position="relative">
          <TimePicker
            placement="bottom-start"
            initialTime={
              sendTiming.timeOfDay ? parseISO(sendTiming.timeOfDay) : set(new Date(), { hours: 9, minutes: 0 })
            }
            onTimeChange={handleTimeOfDayChange}
          />
        </Box>
      </AutomationsFieldContainer>
    </>
  );
};
